import React from "react";
import { Paper, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Skeleton } from "@material-ui/lab";

// Card de resumo padrão do módulo financeiro.
// Uso:
//   <FinanceSummaryCard
//     label="Receitas pagas"
//     value="R$ 1.200,00"
//     caption="opcional"
//     icon={<TrendingUpIcon />}
//     tone="green"        // green | red | blue | amber | grey
//     colorValue          // pinta o valor com a cor do tone
//     loading={false}
//   />
// As cores derivam do "tone" e se adaptam ao tema claro/escuro.

const TONES = {
  green: { light: "#2e7d32", dark: "#81c784", bgLight: "#e8f5e9", bgDark: "rgba(76, 175, 80, 0.18)" },
  red:   { light: "#c62828", dark: "#e57373", bgLight: "#ffebee", bgDark: "rgba(244, 67, 54, 0.18)" },
  blue:  { light: "#1565c0", dark: "#64b5f6", bgLight: "#e3f2fd", bgDark: "rgba(33, 150, 243, 0.18)" },
  amber: { light: "#f57f17", dark: "#ffd54f", bgLight: "#fff8e1", bgDark: "rgba(255, 193, 7, 0.18)" },
  grey:  { light: "#757575", dark: "#bdbdbd", bgLight: "#f5f5f5", bgDark: "rgba(255, 255, 255, 0.08)" },
};

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 12,
    padding: theme.spacing(2.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    height: "100%",
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  label: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  value: {
    fontSize: "1.15rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
}));

const FinanceSummaryCard = ({
  label,
  value,
  caption,
  captionColor,
  icon,
  tone = "grey",
  colorValue = false,
  loading = false,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const toneColors = TONES[tone] || TONES.grey;
  const mainColor = isDark ? toneColors.dark : toneColors.light;
  const bgColor = isDark ? toneColors.bgDark : toneColors.bgLight;

  return (
    <Paper className={classes.card} elevation={0} variant="outlined">
      {icon && (
        <div className={classes.iconCircle} style={{ backgroundColor: bgColor }}>
          {React.cloneElement(icon, {
            style: { ...(icon.props.style || {}), color: mainColor, fontSize: 24 },
          })}
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <Typography className={classes.label}>{label}</Typography>
        {loading ? (
          <Skeleton variant="text" width={110} height={28} />
        ) : (
          <Typography
            className={classes.value}
            style={colorValue ? { color: mainColor } : undefined}
          >
            {value}
          </Typography>
        )}
        {!loading && caption && (
          <Typography
            variant="caption"
            style={{ color: captionColor || theme.palette.text.secondary, fontWeight: 500 }}
          >
            {caption}
          </Typography>
        )}
      </div>
    </Paper>
  );
};

export default FinanceSummaryCard;
